/**
 * PQ Scale Transform — 7-Point to 9-Point
 *
 * Linearly rescales PQ predictions from the native 1–7 ordinal scale
 * to a 1–9 hedonic scale when configured by producer preferences.
 *
 * Formula: PQ₉ = (PQ₇ - 1) × (8/6) + 1
 *
 * See: pq-scale-transform.md
 */

export type PqScaleType = '7pt' | '9pt';

/**
 * Transform a PQ value based on the target scale.
 * When scale is '7pt', the value is returned unchanged.
 * When scale is '9pt', applies the linear rescale from [1,7] → [1,9].
 *
 * @param pqValue The raw PQ value on the 7-point scale
 * @param scale The target scale type
 * @returns The transformed PQ value
 */
export function transformPq(pqValue: number, scale: PqScaleType): number {
  if (scale === '9pt') {
    return (pqValue - 1) * (8 / 6) + 1;
  }
  return pqValue;
}

/**
 * Transform and format a PQ value for display.
 *
 * @param pqValue The raw PQ value (number or string) on the 7-point scale
 * @param scale The target scale type
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted string of the transformed PQ value
 */
export function formatPq(
  pqValue: number | string,
  scale: PqScaleType,
  decimals = 2,
): string {
  const numeric = typeof pqValue === 'string' ? parseFloat(pqValue) : pqValue;
  if (isNaN(numeric)) return '';
  return transformPq(numeric, scale).toFixed(decimals);
}

/**
 * Returns the display label for the PQ scale (e.g., "PQ (9-pt)" or "PQ").
 *
 * @param scale The target scale type
 * @returns Label string
 */
export function pqScaleLabel(scale: PqScaleType): string {
  // Use non-breaking space to prevent column header wrapping
  return scale === '9pt' ? 'PQ\u00A0(9pt)' : 'PQ';
}

/**
 * Returns the max value of the current PQ scale.
 */
export function pqScaleMax(scale: PqScaleType): number {
  return scale === '9pt' ? 9 : 7;
}

/**
 * Returns the min value of the current PQ scale.
 */
export function pqScaleMin(scale: PqScaleType): number {
  return 1;
}
